import { lazy, Suspense, useState, useCallback } from 'react';
import Accordion from '@components/ui/Accordion/Accordion';
import Modal from '@components/ui/Modal/Modal';
import { useTranslation } from '@/hooks/useTranslation';
import type { ProfileMode, ResolvedFormEntry } from './types';
import styles from './TabFormsContent.module.scss';

const FormRenderer = lazy(() => import('./FormRenderer'));

// Edit (pencil) icon
function EditIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M8.25 3H3C2.60218 3 2.22064 3.15804 1.93934 3.43934C1.65804 3.72064 1.5 4.10218 1.5 4.5V15C1.5 15.3978 1.65804 15.7794 1.93934 16.0607C2.22064 16.342 2.60218 16.5 3 16.5H13.5C13.8978 16.5 14.2794 16.342 14.5607 16.0607C14.842 15.7794 15 15.3978 15 15V9.75M13.875 1.875C14.1734 1.57663 14.578 1.40901 15 1.40901C15.422 1.40901 15.8266 1.57663 16.125 1.875C16.4234 2.17337 16.591 2.57805 16.591 3C16.591 3.42195 16.4234 3.82663 16.125 4.125L9 11.25L6 12L6.75 9L13.875 1.875Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface TabFormsContentProps {
  forms: ResolvedFormEntry[];
  mode: ProfileMode;
  /** The object type for form data API calls (e.g., 'TENANT') */
  objectType: string;
  /** The object ID for form data API calls (e.g., the tenant's ID) */
  objectId: string;
}

function TabFormsContent({ forms, mode, objectType, objectId }: TabFormsContentProps) {
  const { t } = useTranslation();
  const [editingFormId, setEditingFormId] = useState<string | null>(null);
  // Incremented after a successful save to force remount of view FormRenderers.
  // @formio/react's <Form> ignores submission prop changes after mount,
  // so a key change is the only way to pick up saved data (e.g., file uploads).
  const [saveVersion, setSaveVersion] = useState(0);

  // Filter forms based on access level for tenant mode
  const visibleForms = mode.kind === 'tenant'
    ? forms.filter((f) => f.accessLevel !== 'NO_ACCESS')
    : forms;

  const editingForm = visibleForms.find((f) => f.formConfigId === editingFormId);

  // Can edit: admin always, tenant only with READ_WRITE
  const canEdit = useCallback(
    (form: ResolvedFormEntry) => {
      if (mode.kind === 'admin') return true;
      return form.accessLevel === 'READ_WRITE';
    },
    [mode.kind],
  );

  const handleOpenEdit = useCallback((formConfigId: string) => {
    setEditingFormId(formConfigId);
  }, []);

  const handleCloseModal = useCallback(() => {
    setEditingFormId(null);
  }, []);

  const handleSaved = useCallback(() => {
    setSaveVersion((v) => v + 1);
    setEditingFormId(null);
  }, []);

  if (visibleForms.length === 0) {
    return (
      <div className={styles.emptyState}>
        {t('K_NO_FORMS_IN_TAB', 'No forms available in this tab.')}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {visibleForms.map((form) => {
        const editable = canEdit(form);

        return (
          <Accordion
            key={`${form.formConfigId}-${saveVersion}`}
            title={form.formName}
            variant="filled"
            whiteBody
            defaultExpanded
            action={
              editable ? (
                <button
                  type="button"
                  className={styles.editButton}
                  onClick={() => handleOpenEdit(form.formConfigId)}
                  aria-label={`Edit ${form.formName}`}
                >
                  <EditIcon />
                </button>
              ) : undefined
            }
          >
            <Suspense
              fallback={<div style={{ padding: '16px 0', color: '#888' }}>{t('K_LOADING_FORM', 'Loading form...')}</div>}
            >
              <FormRenderer
                formConfigId={form.formConfigId}
                readOnly
                renderMode="html"
                objectType={objectType}
                objectId={objectId}
              />
            </Suspense>
          </Accordion>
        );
      })}

      {/* Edit modal */}
      <Modal
        isOpen={editingFormId !== null}
        onClose={handleCloseModal}
        title={editingForm?.formName ?? t('K_MODAL_EDIT_FORM', 'Edit Form')}
        size="lg"
      >
        {editingFormId && (
          <Suspense
            fallback={<div style={{ padding: '16px 0', color: '#888' }}>{t('K_LOADING_FORM', 'Loading form...')}</div>}
          >
            <FormRenderer
              formConfigId={editingFormId}
              readOnly={false}
              renderMode="form"
              objectType={objectType}
              objectId={objectId}
              onSaved={handleSaved}
            />
          </Suspense>
        )}
      </Modal>
    </div>
  );
}

export default TabFormsContent;
